import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useError } from '../../context/ErrorContext';
import { MessageCircle, Check } from 'lucide-react'; // Eliminé AlertCircle porque no se usaba

interface WhatsAppNumberFormProps {
  onClose?: () => void;
  showInSettings?: boolean;
}

// --- Constante externa ---
const COUNTRIES = [
  { code: 'MX', name: 'México', prefix: '+52' },
  { code: 'US', name: 'United States', prefix: '+1' }
];

const WhatsAppNumberForm: React.FC<WhatsAppNumberFormProps> = ({ 
  onClose,
  showInSettings = false
}) => {
  const { currentUser, updateBusinessInfo } = useAuth();
  const { addError, clearErrors, addValidationError } = useError();
  
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+52');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('MX');
  const [isExiting, setIsExiting] = useState(false);

  // Initialize form with existing data if available
  useEffect(() => {
    if (currentUser?.phoneNumber) {
      setPhoneNumber(currentUser.phoneNumber);
    }
    if (currentUser?.countryCode) {
      setCountryCode(currentUser.countryCode);
      // Set selected country based on country code
      if (currentUser.countryCode === '+1') {
        setSelectedCountry('US');
      } else if (currentUser.countryCode === '+52') {
        setSelectedCountry('MX');
      }
    }
  }, [currentUser]);

  const handleCountryChange = (code: string) => {
    setSelectedCountry(code);
    setCountryCode(COUNTRIES.find(c => c.code === code)?.prefix || '+52');
  };

  const validatePhoneNumber = (number: string) => {
    const digits = number.replace(/\D/g, '');
    return digits.length === 10;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentUser?.uid) {
      addError('No se pudo identificar al usuario', 'auth', 'whatsapp-form');
      return;
    }

    try {
      setLoading(true);
      clearErrors();

      // Validate phone number
      if (!phoneNumber.trim() || phoneNumber.length !== 10) {
        addValidationError('El número de teléfono debe tener exactamente 10 dígitos', 'phoneNumber');
        setLoading(false);
        return;
      }

      // Persistencia del teléfono del dueño → vía contexto (estado local; el guardado
      // server-side del perfil se cablea en Inc11/WhatsApp). Antes escribía a Firestore.
      if (updateBusinessInfo && currentUser.businessId) {
        await updateBusinessInfo(currentUser.businessId, {
          phoneNumber: phoneNumber.trim(),
          countryCode: countryCode.trim()
        });
      }

      // Set success state
      setSuccess(true);
      
      // Auto-close after success if not in settings
      if (!showInSettings) {
        // Wait longer before starting exit animation
        setTimeout(() => {
          setIsExiting(true);
          
          // Wait for exit animation to complete before actually closing
          setTimeout(() => {
            if (onClose) onClose();
          }, 600); // Wait for animation (matched with CSS/Tailwind duration approx)
        }, 3000); // Show thank you message for 3 seconds
      }
    } catch (error) {
      console.error('Error saving WhatsApp number:', error);
      addError(
        'No se pudo guardar el número de WhatsApp', 
        'unknown', 
        'whatsapp-form'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`bg-surface rounded-lg shadow-md overflow-hidden ${showInSettings ? '' : 'border border-blue-200 dark:border-blue-500/30'} ${isExiting ? 'animate-fade-out' : 'animate-fade-in'} transition-all duration-500`}>
      <div className="px-4 py-5 sm:p-6">
        <div className="sm:flex sm:items-start">
          <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-500/20 sm:mx-0 sm:h-10 sm:w-10">
            <MessageCircle className="h-6 w-6 text-accent" aria-hidden="true" />
          </div>
          <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
            <h3 className="text-lg leading-6 font-medium text-content">
              {showInSettings ? 'Actualizar número de WhatsApp' : '¡Mantengamos el contacto!'}
            </h3>
            <div className="mt-2">
              <p className="text-sm text-content-3">
                {showInSettings 
                  ? 'Actualiza tu número de WhatsApp para recibir notificaciones importantes sobre tu cuenta.'
                  : 'Proporciona tu número de WhatsApp para recibir notificaciones importantes sobre tu cuenta y novedades del sistema.'}
              </p>
            </div>
          </div>
        </div>

        {success ? (
          <div className="mt-5 sm:mt-4 animate-fade-in">
            <div className="bg-green-50 dark:bg-green-500/10 border-l-4 border-green-400 p-4 mt-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Check className="h-5 w-5 text-green-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-800 dark:text-green-300">
                    ¡Gracias por compartir tu número de WhatsApp!
                  </p>
                  <p className="text-xs text-green-700 dark:text-green-300 mt-1">
                    Solo lo utilizaremos para comunicaciones importantes relacionadas con tu cuenta.
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-5 sm:mt-4">
            <div className="mb-4">
              <label htmlFor="phoneNumber" className="block text-sm font-medium text-content-2">
                Número de WhatsApp <span className="text-red-500">*</span>
              </label>
              <div className="mt-1 flex">
                <div className="relative">
                  <select
                    value={selectedCountry}
                    onChange={(e) => handleCountryChange(e.target.value)}
                    className="appearance-none h-full rounded-l-md border block pl-3 pr-8 py-2 border-line focus:outline-none focus:ring-accent focus:border-blue-500 sm:text-sm"
                  >
                    {COUNTRIES.map(country => (
                      <option key={country.code} value={country.code}>
                        {country.code === 'MX' ? '🇲🇽' : '🇺🇸'} {country.prefix}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-content-2">
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                <input
                  type="tel"
                  name="phoneNumber"
                  id="phoneNumber"
                  required
                  value={phoneNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    if (value.length <= 10) {
                      setPhoneNumber(value);
                    }
                  }}
                  className="flex-1 min-w-0 block w-full px-3 py-2 rounded-r-md focus:ring-accent focus:border-blue-500 sm:text-sm border-line"
                  placeholder="10 dígitos"
                />
              </div>
              {phoneNumber && !validatePhoneNumber(phoneNumber) && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  El número debe tener exactamente 10 dígitos
                </p>
              )}
              <p className="mt-1 text-xs text-content-3">
                Solo lo utilizaremos para notificaciones importantes sobre tu cuenta.
              </p>
            </div>

            <div className="sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                disabled={loading || (phoneNumber.length !== 10)}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Guardando...' : 'Guardar'}
              </button>
              {showInSettings && (
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-line shadow-sm px-4 py-2 bg-surface text-base font-medium text-content-2 hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent sm:mt-0 sm:w-auto sm:text-sm"
                  onClick={onClose}
                >
                  Cancelar
                </button>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default WhatsAppNumberForm;